import PageHeader from '../../components/PageHeader';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { WorkOrder } from '../../types';
import { workOrderAPI } from '../../services/api';
import { format } from 'date-fns';

const WorkOrdersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);

  const loadWorkOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await workOrderAPI.getAllWorkOrders({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        page: 1,
        limit: 50,
      });
      setWorkOrders(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkOrders();
  }, [statusFilter, priorityFilter]);

  const filteredWorkOrders = workOrders.filter(workOrder => {
    if (search) {
      const query = search.toLowerCase();
      return (
        workOrder.title.toLowerCase().includes(query) ||
        workOrder.description.toLowerCase().includes(query) ||
        workOrder.device.plateNumber?.toLowerCase().includes(query) ||
        workOrder.device.imei.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getStatusColor = (status: WorkOrder['status']) => {
    switch (status) {
      case 'open':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: WorkOrder['priority']) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: WorkOrder['status']) => {
    switch (status) {
      case 'open':
        return <ScheduleIcon />;
      case 'in_progress':
        return <AssignmentIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      case 'cancelled':
        return <WarningIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  const openCount = workOrders.filter(w => w.status === 'open').length;
  const inProgressCount = workOrders.filter(w => w.status === 'in_progress').length;
  const completedCount = workOrders.filter(w => w.status === 'completed').length;
  const overdueCount = workOrders.filter(w => 
    w.dueDate && new Date(w.dueDate) < new Date() && w.status !== 'completed'
  ).length;

  return (
    <Box>
      <PageHeader title="Work Orders" description="Track maintenance tasks, service progress, and work orders" icon="maintenance" />

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Open Work Orders
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    {openCount}
                  </Typography>
                </Box>
                <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    In Progress
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {inProgressCount}
                  </Typography>
                </Box>
                <AssignmentIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Completed
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {completedCount}
                  </Typography>
                </Box>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Overdue
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {overdueCount}
                  </Typography>
                </Box>
                <WarningIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                placeholder="Search work orders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={!isMobile && <RefreshIcon />}
                onClick={loadWorkOrders}
                size={isMobile ? 'small' : 'medium'}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={!isMobile && <AddIcon />}
                onClick={() => setOpenAdd(true)}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? 'New' : 'New Work Order'}
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip size={isMobile ? 'small' : 'medium'} label={`All (${workOrders.length})`} />
            <Chip size={isMobile ? 'small' : 'medium'} color="warning" label={`Open (${openCount})`} />
            <Chip size={isMobile ? 'small' : 'medium'} color="info" label={`In Progress (${inProgressCount})`} />
            <Chip size={isMobile ? 'small' : 'medium'} color="success" label={`Completed (${completedCount})`} />
            <Chip size={isMobile ? 'small' : 'medium'} color="error" label={`Overdue (${overdueCount})`} />
          </Stack>
        </CardContent>
      </Card>

      {/* Work Orders Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ width: '100%', overflowX: 'auto' }}><Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                <TableRow>
                  <TableCell>Work Order</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWorkOrders.map((workOrder) => (
                  <TableRow key={workOrder._id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {workOrder.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {workOrder.description.length > 50 
                            ? `${workOrder.description.substring(0, 50)}...` 
                            : workOrder.description
                          }
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {workOrder.device.plateNumber || 'No Plate'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {workOrder.device.make} {workOrder.device.modelYear}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={getStatusColor(workOrder.status) as any}
                        label={workOrder.status.replace('_', ' ').toUpperCase()}
                        icon={getStatusIcon(workOrder.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={getPriorityColor(workOrder.priority) as any}
                        label={workOrder.priority.toUpperCase()}
                      />
                    </TableCell>
                    <TableCell>
                      {workOrder.assignedTo || 'Unassigned'}
                    </TableCell>
                    <TableCell>
                      {workOrder.dueDate ? format(new Date(workOrder.dueDate), 'MMM dd, yyyy') : 'No due date'}
                    </TableCell>
                    <TableCell>
                      {workOrder.estimatedCost ? `$${workOrder.estimatedCost}` : 'Not estimated'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Details">
                          <IconButton size="small" color="primary">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Work Order">
                          <IconButton size="small" color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Work Order">
                          <IconButton size="small" color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Work Order Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="md">
        <DialogTitle>Create New Work Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Work order creation form will be implemented here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkOrdersPage;
